import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { showNotification } from 'react-admin';
import { withStyles } from '@material-ui/styles';
import { withDataProvider } from 'ra-core';
import { parseQueryString } from '../../../utils';
import { BasicInfo, Languages } from './steps';

const styles = {
  container: {
    marginTop: '-24px',
  },
};
const UniversalConsentItemCreateEdit = ({ classes, dataProvider, history, showNotification, ...rest }) => {
  const [info, setInfo] = useState({ name: '', required: false, languagesIDs: [], universalConsentItemTranslations: [] });
  const [solutionID, setSolutionID] = useState('');
  const [step, setStep] = useState('basic');
  const existingItemID = rest.match.params.id;

  const saveBasicInfo = data => {
    const universalConsentItemTranslations = data.languagesIDs.map(language => (
      info.universalConsentItemTranslations.find(translation => translation.language === language)
      ||
      { language: language, longText: '', shortText: '' }
    ));
    setInfo({ ...data, universalConsentItemTranslations });
    setStep('languages');
  };


  const createItem = async({ name, required, universalConsentItemType, universalConsentItemTranslations }) => {
    try {
      const payload = { name, required, universalConsentItemType, universalConsentItemTranslations, universalConsentSolution: solutionID };
      await dataProvider.create('universal_consent_items', { data: payload });
      const encodedURI = encodeURIComponent(solutionID);
      showNotification('universal_consent_item_save_success');
      history.push(`/universal_consent_solutions/${encodedURI}`);
    } catch (error) {
      showNotification('universal_consent_item_save_fail',
        'warning',
        {
          messageArgs: {
            message: error.message || '',
          },
        });
    }
  };

  const updateItem = async({ languagesIDs, ...payload }) => {
    try {
      await dataProvider.update('universal_consent_items', { id: payload.id, data: payload });
      const encodedURI = encodeURIComponent(solutionID);
      showNotification('universal_consent_item_save_success');
      history.push(`/universal_consent_solutions/${encodedURI}`);
    } catch (error) {
      showNotification('universal_consent_item_save_fail',
        'warning',
        {
          messageArgs: {
            message: error.message || '',
          },
        });
    }
  };

  const onSave = async data => {
    const sanitizedTranslations = data.universalConsentItemTranslations.map(translation => {
      translation.longText = translation.longText || '';
      translation.shortText = translation.shortText || '';
      return translation;
    });
    const sanitizedData = { ...data, universalConsentItemTranslations: sanitizedTranslations };
    if (existingItemID) {
      await updateItem(sanitizedData);
    } else {
      await createItem(sanitizedData);
    }
  };

  const onBack = data => {
    setInfo(data);
    setStep('basic');
  };

  const initializeForm = async() => {
    try {
      const decodedURI = decodeURIComponent(existingItemID);
      const { data: item } = await dataProvider.getOne('universal_consent_items', { id: decodedURI });
      const { data: translations } =
        await dataProvider.getMany('universal_consent_item_translations', { ids: item.universalConsentItemTranslations });
      const newInfo = {
        ...item,
        universalConsentItemTranslations: translations,
        languagesIDs: translations.map(({ language }) => language),
      };
      setInfo(newInfo);
      setSolutionID(item.universalConsentSolution);
    } catch (e) {
      showNotification('universal_consent_item_load_fail', 'warning');
      await checkSolution(true);
    }
  };

  const checkSolution = async(redirect = false) => {
    const { solutionID } = parseQueryString(history.location.search);
    try {
      await dataProvider.getOne('universal_consent_solutions', { id: solutionID });
      if (redirect) {
        const encodedURI = encodeURIComponent(solutionID);
        history.push(`/universal_consent_solutions/${encodedURI}`);
      } else {
        setSolutionID(solutionID);
      }
    } catch (error) {
      showNotification('missing_universal_consent_solution', 'warning');
      history.push('/universal_consent_solutions/');
    }
  };

  useEffect(() => {
    if (existingItemID) {
      initializeForm();
    } else {
      checkSolution();
    }

  }, [history.location.search]);

  return (
    <div className={classes.container}>
      {
        step === 'basic' ?
          <BasicInfo {...rest} data={info} onSubmit={saveBasicInfo} solutionID={solutionID}/>
          : step === 'languages' ?
            <Languages data={info} onSave={onSave} onBack={onBack}/>
            : null
      }
    </div>

  );
};

export default withStyles(styles)(
  connect(null, { showNotification })(withDataProvider(UniversalConsentItemCreateEdit)),
);
